/* ============================================================
   SAM-198 — shared service-editor state & payload logic.

   Extracted from the pre-SAM-198 ServiceDetailDrawer.tsx so the new
   full-page editor (src/pages/ServiceEditor/ServiceEditorPage.tsx) and the
   old drawer could, during the transition, both build on the exact same
   form state and produce the exact same save payload. The pure functions
   (serviceToForm / formToServiceData) and every updater below are moved
   verbatim — behaviour, field names and payload shape are unchanged from
   the drawer; only where the code lives changed.
   ============================================================ */
import {
  emptyRateGroup, pricesByGroupToForm, pricesByGroupToPayload, rateGroupsToForm, rateGroupsToPayload,
  type RateGroupForm,
} from '../utils/rateGroups';
import React, { useEffect, useState } from 'react';
import {
  useCreateService, useUpdateService, useDeleteService,
  useSyncServiceToEngine, isBookingEngineSyncEnabled,
  isServiceActive,
  type Service, type BookingOption, type OperatingHours, type ServicePricing,
  type ServiceSubtype, type ServiceResourceConfig, type ServiceAvailabilityPeriod, type ServiceAddOn,
  SERVICE_SUBTYPE_SCHEDULING_TYPE,
  parseISODurationToMinutes, minutesToISODuration,
  parseISODurationToHours, hoursToISODuration,
  parseISODurationToDays, daysToISODuration,
} from './useServices';
import type { RoleCapsForm } from '../components/ServicePricingMatrix';
import {
  parseSeasons, serializeSeasons, validateSeasonForms, nextSeasonId, removeGroupFromSeasons, renameGroupInSeasons, emptySeasonPrice, setSeasonEveryYear, SEASON_SCALAR_FIELDS,
  type SeasonForm, type SeasonPriceField,
} from '../components/serviceSeasons';
import { uniqueResourceKey, type ResourceForm, type ResourceFormField } from '../components/ServiceResourcesEditor';
import type { ClosureForm } from '../components/ServiceClosuresEditor';
import type { TierGroupForm } from '../components/ServiceTierGroupsEditor';
import { unitPricingIssues, carryOverToUnit } from '../pages/ServiceEditor/unitPricing';
import { isEventSubtype, isEventSize, suggestEventSize, EVENT_SIZE_BANDS, type EventSize } from '../utils/eventSize';
import type { PriceUnit } from './useServices';
import { badgeForSave, badgeSaveIssue, isServiceBadge, type ServiceBadgeKey } from '../utils/serviceBadge';
import { getApiErrorMessage } from '../utils/apiErrors';
import { seasonSaveIssue, formatSeasonSaveIssue, type SeasonSaveIssue } from '../utils/pricingErrors';
import { validateDayWindows, buildDayWindowsForSave, type TimeWindow } from '../utils/operatingWindows';
import { penaltyPercentFromSettings, penaltyPercentError, penaltyPercentForSave } from '../utils/cancellationPenalty';
import { hoursSourceOf, hoursSourceForSave, type HoursSource } from '../utils/serviceHoursSource';
import {
  SUBTYPE_BACKEND_MAP, DEFAULT_UNIT_SEEDS, usesUnits, type CreateServiceType,
} from '../pages/ServiceEditor/create/serviceCreateTaxonomy';

// --- Constants ---

export const STATUS_OPTIONS = [
  { label: 'Active', id: 'active' },
  { label: 'Inactive', id: 'inactive' },
];

export const CURRENCY_OPTIONS = [
  { label: 'USD', id: 'USD' },
  { label: 'DOP', id: 'DOP' },
];

// SAM-196 — how a guest books this service.
// SAM-198 follow-up (sam-lambda-functions PR #420) — 'flexible' (variable
// duration between a min/max, e.g. hourly Spaces & Equipment) is now a real
// option here: `equipment`'s default backend scheduling type. There is
// still no dedicated min/max-duration UI for it — same "accepted, no
// dedicated UI yet" caveat `ServiceType`'s own doc carries in useServices.ts
// — but the read-only scheduling chip (SchedulingSection.tsx) and every
// `booking_type === 'flexible'` check below now handle it explicitly
// instead of silently mis-deriving 'fixed'.
export const BOOKING_TYPE_OPTIONS = [
  { label: 'Time slot', id: 'fixed' },
  { label: 'Whole day', id: 'day' },
  { label: 'Flexible duration', id: 'flexible' },
];

// SAM-545 (N10) — catalog subtype picker. '' = a plain/custom service, no
// subtype (unchanged default behaviour). The rest are the closed vocabulary
// from SAM-533 (`functions/booking/catalog_taxonomy.py`, widened by PR #420
// to match Rodrigo's "12.4 Services admin UI" prototype — see
// serviceCreateTaxonomy.ts's CREATE_SERVICE_SUBTYPES for the create flow's
// own copy of these); each one locks Booking Type to the scheduling type the
// backend requires for it (see SERVICE_SUBTYPE_SCHEDULING_TYPE), so the two
// can never be saved out of sync.
export const SUBTYPE_OPTIONS: Array<{ id: '' | ServiceSubtype; label: string; caption: string }> = [
  { id: '', label: 'Custom / none', caption: 'A regular service — choose Booking Type freely below.' },
  { id: 'cabana', label: 'Cabana', caption: 'Booked as a whole day, like a day pass — capacity is per date. Uses units (Cabanas).' },
  { id: 'minconsumo', label: 'Minimum Consumption', caption: 'Redeemable the same day on property. Not a wallet credit — redemption stays a manual/POS step.' },
  { id: 'teetime', label: 'Tee Time', caption: 'Booked as a fixed time slot — the tee sheet reserves the interval, not the round. Uses units (Starting tees).' },
  { id: 'private', label: 'Appointments (1-on-1)', caption: "A provider's own schedule, one guest at a time — a fixed session length." },
  { id: 'class', label: 'Classes (Groups)', caption: 'A fixed recurring schedule, group capacity.' },
  { id: 'stay', label: 'Stay / Multi-Day', caption: 'Villas, retreats, accommodations — an admin-set min/max night range. Uses units.' },
  { id: 'equipment', label: 'Spaces & Equipment', caption: 'By the hour or session by default (flexible duration); can instead be set up as a full-day rental. Uses units.' },
  { id: 'tables', label: 'Tables / Seating', caption: 'A restaurant table booking, fixed reservation length. Uses units (Tables).' },
  { id: 'daypass', label: 'Day Pass / Access', caption: 'Day pass or timed entry to the venue — one day, venue-wide capacity.' },
  { id: 'single', label: 'Single-day Event', caption: 'One date, a set start time.' },
  { id: 'multiday', label: 'Multi-day Event', caption: 'Spans several days (festivals, conferences) — an admin-set min/max day range.' },
];

export const DAYS_OF_WEEK = [
  { key: 'monday', label: 'MON' },
  { key: 'tuesday', label: 'TUE' },
  { key: 'wednesday', label: 'WED' },
  { key: 'thursday', label: 'THU' },
  { key: 'friday', label: 'FRI' },
  { key: 'saturday', label: 'SAT' },
  { key: 'sunday', label: 'SUN' },
];

// --- Types ---

export interface OperatingHourForm {
  open: string;
  close: string;
  enabled: boolean;
  // SAM-198 — recurring class schedules: extra windows on the same weekday
  // (e.g. Yoga Monday 08:00-09:00 AND 18:00-19:00). Capacity is uniform
  // across windows (settings.intervals.max_per_slot) — `capacity`/`instructor`
  // below are label-only, see `TimeWindow`'s doc in utils/operatingWindows.ts.
  extra_windows: TimeWindow[];
  // SAM-546 — Sessions editor label-only metadata for THIS day's first/base
  // window (open/close above). Same label-only caveat as TimeWindow's.
  label?: string;
  capacity?: string;
  instructor?: string;
}

export interface BookingOptionForm {
  id: string;
  label_en: string;
  label_es: string;
  short_desc_en: string;
  short_desc_es: string;
  price: string;
  currency: string;
  min_amount: string;
  max_amount: string;
  // SAM-195: keyed by user_type_id (string). Empty/absent = flat price.
  prices_by_role: Record<string, string>;
  // SAM-588: keyed by rate group id. Empty/absent cell = not sold to that group.
  prices_by_group: Record<string, string>;
  // SAM-591 — what one guest of this type pays beyond the unit's included pax (only used when
  // `price_unit === 'unit'`; `price` is ignored then).
  extra_guest_price: string;
  // SAM-592: flat event price per size band (s/m/l/xl), only used when the service is an event
  // (Single-day / Multi-day subtype -> pricing.price_unit "event"). Empty cell = not sold at that size.
  prices_by_event_size: Record<string, string>;
}

// SAM-590 — one Add-Ons editor row. Numbers stay strings (same convention as
// BookingOptionForm) so an empty field is distinguishable from a typed 0.
export interface AddOnForm {
  id: string;
  label_en: string;
  label_es: string;
  price: string;
  currency: string;
  max_qty: string;
  required: boolean;
  prices_by_role: Record<string, string>;
}

interface DescriptionBlock {
  order: number;
  title: string;
  content: string;
}

export interface FormData {
  name_en: string;
  name_es: string;
  description_en: string;
  description_es: string;
  status: string;
  business_id: string;
  // Operating Hours
  operating_hours: Record<string, OperatingHourForm>;
  // SAM-656 — settings.hours_source. 'business' = follow the business's Schedule; the service's own
  // operating_hours above stay in the form (and in the save payload) untouched, so switching back
  // to 'custom' restores them.
  hours_source: HoursSource;
  // Booking Type — 'fixed': a time slot is chosen; 'day': a whole date is
  // chosen, no time-of-day selector (SAM-196).
  booking_type: 'fixed' | 'day' | 'flexible';
  // SAM-545 (N10) — catalog subtype (SAM-533). '' = none/custom.
  subtype: '' | ServiceSubtype;
  // SAM-533 — service_group / service_type FK lookups (`service_group_id`,
  // `service_type_id` on the `service` row). Kept as strings, same as
  // pricing_default_user_type_id, so an unset select is distinguishable from
  // id 0. '' = no group/type set.
  service_group_id: string;
  service_type_id: string;
  // Booking Settings
  interval_duration: string;
  max_per_slot: string;
  start_padding: string;
  end_padding: string;
  // SAM-198 field-parity pass — 'fixed' only, optional. See IntervalSettings.bookable_interval.
  bookable_interval: string;
  // SAM-198 field-parity pass — 'day' only, optional. settings.min_days / settings.max_days,
  // read by engine_sync.py::_build_service_payload (type == day). See
  // ENGINE_SCHEDULING_FIELD_PATHS below for the full field <-> engine-reader map.
  min_days: string;
  max_days: string;
  // Booking Window
  booking_window_start: string;
  booking_window_end: string;
  // Max Capacity
  max_capacity: string;
  // SAM-592 — event size band (s/m/l/xl), stored in settings.event_size. '' = the admin has not
  // chosen one: the UI shows (and the save payload persists) the band suggested from max_capacity.
  event_size: EventSize | '';
  // SAM-657 — settings.badge. '' = "Suggested": nothing is stored and the setup's badge is used.
  badge: ServiceBadgeKey | '';
  // Cancellation Policy
  cancellation_time_limit: string;
  cancellation_penalty_fee: boolean;
  // SAM-466 — settings.cancellation_policy.penalty_percent, 0-100 integer as text. '' = not set: the
  // backend then keeps the credit at 100% even with penalty_fee on (cancellation_policy.py).
  cancellation_penalty_percent: string;
  // SAM-198 Policies & Add-Ons parity pass (Rodrigo's 17 Sep proposal, `secBooking()` in
  // SAM-services-admin-prototype/preview-proposed/services-flow.js). None of these have a
  // backend reader today — no consumer in sam-lambda-functions@origin/dev (checked 2026-09-25:
  // no `approval_flow`/`payment_requirement`/`waitlist`/`age_restriction`/`terms` in
  // functions/booking/, only `cancellation_policy.time_limit` and `.penalty_fee` are read, by
  // functions/booking/cancellation_policy.py::evaluate). Saved under settings.booking_policy,
  // which nothing reads yet either, so round-tripping them is safe — but the UI must say so
  // (see PoliciesSection's "not enforced yet" notices) rather than let an admin believe filling
  // them changes booking behaviour.
  waitlist_enabled: boolean;
  approval_flow: 'instant' | 'request';
  payment_requirement: 'free' | 'cc_hold' | 'deposit' | 'full';
  payment_amount: string;
  payment_hold_method: 'cc_auth' | 'charge_refund';
  // Prototype's "Inside/Outside the window" — the engine/payments side only ever reads
  // `cancellation_policy.time_limit` as a hard cutoff; there is no auto/request/none branch.
  cancellation_inside_window: 'auto' | 'request';
  cancellation_outside_window: 'auto' | 'request' | 'none';
  age_restriction: string;
  terms_conditions: string;
  // Booking Options
  booking_options: BookingOptionForm[];
  // SAM-590 — Add-Ons (Policies & Add-Ons section). See AddOnForm.
  add_ons: AddOnForm[];
  // Role Pricing (SAM-195)
  pricing_default_user_type_id: string;
  pricing_roles: Record<string, RoleCapsForm>;
  // SAM-195 follow-up — pricing.tier_groups: a cap on the SUMMED quantity across several
  // booking options, plus optionally N guests included in the base price.
  tier_groups: TierGroupForm[];
  // SAM-588 — pricing.rate_groups (tariffs with eligibility + quota). Wins over role pricing.
  rate_groups: RateGroupForm[];
  // SAM-589 — pricing.seasons (price per date range).
  seasons: SeasonForm[];
  // SAM-591 — pricing.price_unit + pricing.unit defaults. 'person' = per-option price (default).
  price_unit: PriceUnit;
  unit_price: string;
  unit_included_pax: string;
  unit_max_pax: string;
  // SAM-546 (N11) — N resources per service (settings.resources). Empty = single implicit
  // resource, unchanged pre-SAM-546 behaviour.
  resources: ResourceForm[];
  // SAM-546 (N11) — one-off closures (restrictions.availability, status "unavailable").
  closures: ClosureForm[];
  // Media (SAM-1009, option A: cover + colour) — the cover is background_img_url; the editor never writes logo_url.
  background_img_url: string;
  background_color: string;
}

// SAM-198 "PASO DE SCHEDULING UNIFICADO" (field <-> engine-reader parity, backend research done
// against sam-lambda-functions@origin/dev 2026-09-23). Every path here is a `settings.*` /
// top-level key that `formToServiceData` can write AND that the booking engine sync actually
// reads — see `functions/booking/engine_sync.py::_build_service_payload` (dotted paths below are
// relative to `settings`, except `top:*` which are top-level Service columns). Kept as a single
// source of truth so the round-trip test can assert the two stay in sync:
//   - `top:type`                          -> engine Service.type
//   - `intervals.duration`                -> engine Service.duration (type=fixed)
//   - `intervals.max_per_slot`            -> default-resource Resource.max_simultaneous_bookings (type=fixed)
//   - `intervals.start_padding`           -> engine Service.buffer_time_before (fixed; engine-only, no local effect)
//   - `intervals.end_padding`             -> engine Service.buffer_time_after (fixed; engine-only, no local effect)
//   - `intervals.bookable_interval`       -> engine Service.bookable_interval (fixed, optional; engine-only)
//   - `start_time` / `end_time`           -> engine Service.start_time/end_time (type=day; not written by this
//                                             editor — operating_hours is the single source of truth for day
//                                             open/close, engine_sync.py falls back to it when settings.start_time
//                                             is absent, so this editor intentionally never writes it directly)
//   - `min_days` / `max_days`             -> engine Service.min_days/max_days (type=day, optional)
//   - `daily_capacity`                    -> default-resource Resource.max_simultaneous_bookings (type=day)
//   - `resources[]`                       -> engine Resource[] (key/name/capacity/max_simultaneous_bookings)
//   - `cancellation_policy.time_limit`    -> engine Service.cancelation_threshold
//   - `top:booking_window_start`          -> engine Service.booking_window_start
//   - `top:booking_window_end`            -> engine Service.booking_window_end
//   - `top:operating_hours`               -> engine RecurringSchedule + ScheduleBlock(s)
//   - `top:restrictions.availability`     -> engine ScheduleBlock(s) (status: "unavailable" only)
// Deliberately NOT in this list (present in the form/payload but NOT read by the engine sync):
//   - `top:max_capacity` — SAM-only venue aforo, never sent to the engine (engine_sync.py comment,
//     2026-09-19 decision) — see the Max Capacity field's caption.
//   - `cancellation_policy.penalty_fee` — SAM/payments-only, explicitly dropped before the engine payload.
export const ENGINE_SCHEDULING_FIELD_PATHS = [
  'top:type',
  'intervals.duration',
  'intervals.max_per_slot',
  'intervals.start_padding',
  'intervals.end_padding',
  'intervals.bookable_interval',
  'min_days',
  'max_days',
  'daily_capacity',
  'resources',
  'cancellation_policy.time_limit',
  'top:booking_window_start',
  'top:booking_window_end',
  'top:operating_hours',
  'top:restrictions.availability',
] as const;

export const DEFAULT_OPERATING_HOURS: Record<string, OperatingHourForm> = {
  monday: { open: '08:00', close: '18:00', enabled: true, extra_windows: [], label: '', capacity: '', instructor: '' },
  tuesday: { open: '08:00', close: '18:00', enabled: true, extra_windows: [], label: '', capacity: '', instructor: '' },
  wednesday: { open: '08:00', close: '18:00', enabled: true, extra_windows: [], label: '', capacity: '', instructor: '' },
  thursday: { open: '08:00', close: '18:00', enabled: true, extra_windows: [], label: '', capacity: '', instructor: '' },
  friday: { open: '08:00', close: '18:00', enabled: true, extra_windows: [], label: '', capacity: '', instructor: '' },
  saturday: { open: '08:00', close: '14:00', enabled: false, extra_windows: [], label: '', capacity: '', instructor: '' },
  sunday: { open: '', close: '', enabled: false, extra_windows: [], label: '', capacity: '', instructor: '' },
};

export const EMPTY_OPTION: BookingOptionForm = {
  id: '',
  label_en: '',
  label_es: '',
  short_desc_en: '',
  short_desc_es: '',
  price: '',
  currency: 'USD',
  min_amount: '1',
  max_amount: '10',
  prices_by_role: {},
  prices_by_group: {},
  extra_guest_price: '',
  prices_by_event_size: {},
};

// SAM-590 — currency defaults to the service's first booking option currency at the call site
// (addAddOn below), not here, since this constant has no service context.
export const EMPTY_ADD_ON: AddOnForm = {
  id: '',
  label_en: '',
  label_es: '',
  price: '',
  currency: 'USD',
  max_qty: '1',
  required: false,
  prices_by_role: {},
};

// SAM-590 — derives a slug id from a label, matching what the backend expects for
// `service.settings.add_ons[].id` (a stable, unique-per-service string key). Lowercase,
// non-alphanumeric runs collapsed to a single '-', trimmed of leading/trailing '-'.
export function slugifyAddOnId(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function buildInitialForm(businessId: string | null): FormData {
  return {
    name_en: '',
    name_es: '',
    description_en: '',
    description_es: '',
    status: 'active',
    business_id: businessId || '',
    operating_hours: { ...DEFAULT_OPERATING_HOURS },
    hours_source: 'custom',
    booking_type: 'fixed',
    subtype: '',
    service_group_id: '',
    service_type_id: '',
    interval_duration: '60',
    max_per_slot: '1',
    start_padding: '0',
    end_padding: '0',
    bookable_interval: '',
    min_days: '',
    max_days: '',
    booking_window_start: '',
    booking_window_end: '',
    max_capacity: '',
    event_size: '',
    badge: '',
    cancellation_time_limit: '24',
    cancellation_penalty_fee: false,
    cancellation_penalty_percent: '',
    waitlist_enabled: false,
    approval_flow: 'instant',
    payment_requirement: 'free',
    payment_amount: '',
    payment_hold_method: 'cc_auth',
    cancellation_inside_window: 'auto',
    cancellation_outside_window: 'request',
    age_restriction: '',
    terms_conditions: '',
    booking_options: [],
    add_ons: [],
    pricing_default_user_type_id: '',
    pricing_roles: {},
    tier_groups: [],
    rate_groups: [],
    seasons: [],
    price_unit: 'person',
    unit_price: '',
    unit_included_pax: '',
    unit_max_pax: '',
    resources: [],
    closures: [],
    background_img_url: '',
    background_color: '#D4D4D4',
  };
}

// SAM-591 — settings.resources[].metadata for one resource row: the per-unit price / included_pax
// / max_pax override (blank = inherit pricing.unit) merged over any other metadata keys the stored
// resource already had. Returns {} when there is nothing to write, so a plain resource's payload
// stays exactly what it was.
export function buildResourceMetadata(r: ResourceForm, unitPriced: boolean): { metadata?: Record<string, unknown> } {
  const metadata: Record<string, unknown> = { ...(r.metadata_rest || {}) };
  if (unitPriced) {
    if ((r.price ?? '').trim() !== '') metadata.price = Number(r.price) || 0;
    if ((r.included_pax ?? '').trim() !== '') metadata.included_pax = Number(r.included_pax) || 0;
    if ((r.max_pax ?? '').trim() !== '') metadata.max_pax = Number(r.max_pax) || 0;
  }
  return Object.keys(metadata).length ? { metadata } : {};
}

// SAM-198 — create flow (src/pages/ServiceEditor/create/**) step 2 "choose a setup" applies its
// picked prototype subtype (e.g. 'stay', 'private', 'daypass' — see serviceCreateTaxonomy.ts's
// CREATE_SERVICE_SUBTYPES, ported from services-flow.js's SERVICE_SUBTYPES) to the form that then
// opens. This is the SAM-198 `applySubtypeDefaults(form, type, subtype)` helper: pure, so the
// create flow and its tests can call it without a hook. `type` is currently unused by the mapping
// itself (SUBTYPE_BACKEND_MAP is keyed by the flat, cross-type-unique subtype code, same as the
// prototype's `subtypeName()`) but is part of the signature per the ticket and kept for forward
// compatibility (e.g. a future type-scoped default).
export function applySubtypeDefaults(
  form: FormData,
  _type: CreateServiceType,
  subtype: string,
): FormData {
  const mapping = SUBTYPE_BACKEND_MAP[subtype];
  // Unknown subtype code: leave the form untouched rather than guess.
  if (!mapping) return form;

  const next: FormData = {
    ...form,
    subtype: (mapping.backendSubtype || '') as FormData['subtype'],
    booking_type: mapping.bookingType,
  };

  // Seed a starting resource row for unit-backed subtypes (tables/cabana/teetime/equipment/stay —
  // see UNIT_LABEL), same idea as the prototype's `applySubtypeDefaults()` seeding `f.units`. Only
  // seeds when the form doesn't already have resources, so re-picking the same subtype (or
  // switching back and forth) never duplicates or clobbers rows the admin has already edited.
  if (usesUnits(subtype) && next.resources.length === 0) {
    const seeds = DEFAULT_UNIT_SEEDS[subtype] || [];
    next.resources = seeds.map((seed) => ({
      key: uniqueResourceKey(seed.name, []),
      name: seed.name,
      capacity: seed.capacity,
      max_simultaneous_bookings: '',
    }));
  }

  // SAM-591 — a cabana is ONE price that includes X people (adults and children mixed) plus a
  // charge per additional person, not per-person tiers. Seeds the prototype numbers (400 / 6
  // included / 8 max; adult 25, child 15 extra) only on an untouched form, so re-picking the
  // subtype never overwrites what the admin already typed.
  if (subtype === 'cabana' && next.price_unit === 'person' && next.booking_options.length === 0) {
    next.price_unit = 'unit';
    next.unit_price = '400';
    next.unit_included_pax = '6';
    next.unit_max_pax = '8';
    next.booking_options = [
      { ...EMPTY_OPTION, id: 'adult', label_en: 'Adult', label_es: 'Adulto', price: '0', extra_guest_price: '25' },
      { ...EMPTY_OPTION, id: 'child', label_en: 'Child', label_es: 'Niño', price: '0', extra_guest_price: '15', min_amount: '0' },
    ];
  }

  // SAM-592 — an event (Single-day / Multi-day) defaults to "per event size". Only from the untouched
  // 'person' default, so an admin who already picked a model keeps it.
  if (isEventSubtype(next.subtype) && next.price_unit === 'person') {
    next.price_unit = 'event';
  }

  return next;
}

export function serviceToForm(service: Service, businessId: string | null): FormData {
  // Parse operating hours from API format to form format
  const opHours: Record<string, OperatingHourForm> = {};
  DAYS_OF_WEEK.forEach(({ key }) => {
    const apiDay = service.operating_hours?.[key];
    if (apiDay) {
      opHours[key] = {
        open: apiDay.open,
        close: apiDay.close,
        enabled: true,
        extra_windows: (apiDay.extra_windows || []).map((w) => ({
          open: w.open,
          close: w.close,
          label: w.label || '',
          capacity: w.capacity || '',
          instructor: w.instructor || '',
        })),
        label: apiDay.label || '',
        capacity: apiDay.capacity || '',
        instructor: apiDay.instructor || '',
      };
    } else {
      opHours[key] = {
        open: DEFAULT_OPERATING_HOURS[key].open,
        close: DEFAULT_OPERATING_HOURS[key].close,
        enabled: false,
        extra_windows: [],
        label: '',
        capacity: '',
        instructor: '',
      };
    }
  });

  // Parse settings
  const intervals = service.settings?.intervals;
  const cancellation = service.settings?.cancellation_policy;
  // SAM-198 Policies parity — settings.booking_policy, not read by the engine (see the FormData
  // comment above).
  const bookingPolicy = service.settings?.booking_policy || {};

  // Parse booking options
  const options: BookingOptionForm[] = (service.booking_options || []).map((opt) => ({
    id: opt.id,
    label_en: opt.label_en,
    label_es: opt.label_es,
    short_desc_en: opt.short_desc_en,
    short_desc_es: opt.short_desc_es,
    price: String(opt.price),
    currency: opt.currency || 'USD',
    min_amount: String(opt.min_amount),
    max_amount: String(opt.max_amount),
    prices_by_role: Object.fromEntries(
      Object.entries(opt.prices_by_role || {}).map(([roleId, price]) => [roleId, String(price)])
    ),
    prices_by_group: pricesByGroupToForm(opt.prices_by_group),
    extra_guest_price: opt.extra_guest_price != null ? String(opt.extra_guest_price) : '',
    prices_by_event_size: Object.fromEntries(
      Object.entries(opt.prices_by_event_size || {}).map(([size, price]) => [size, String(price)])
    ),
  }));

  // SAM-590 — parse settings.add_ons. Absent/empty = no add-ons.
  const addOns: AddOnForm[] = (service.settings?.add_ons || []).map((a) => ({
    id: a.id,
    label_en: a.label_en || '',
    label_es: a.label_es || '',
    price: String(a.price),
    currency: a.currency || 'USD',
    max_qty: String(a.max_qty ?? 1),
    required: Boolean(a.required),
    prices_by_role: Object.fromEntries(
      Object.entries(a.prices_by_role || {}).map(([roleId, price]) => [roleId, String(price)])
    ),
  }));

  // Parse tier groups (SAM-195 follow-up). Numbers become strings so an empty field stays
  // distinguishable from a zero; formToServiceData turns them back.
  const tierGroups: TierGroupForm[] = (service.pricing?.tier_groups || []).map((group) => ({
    id: group.id || '',
    label_es: group.label?.es || '',
    label_en: group.label?.en || '',
    tiers: Array.isArray(group.tiers) ? [...group.tiers] : [],
    max_total: group.max_total != null ? String(group.max_total) : '',
    included: group.included != null ? String(group.included) : '',
  }));

  // Parse role pricing config (SAM-195)
  const pricingRoles: Record<string, RoleCapsForm> = {};
  Object.entries(service.pricing?.roles || {}).forEach(([roleId, cfg]) => {
    pricingRoles[roleId] = {
      max_qty_per_booking: cfg.max_qty_per_booking != null ? String(cfg.max_qty_per_booking) : '',
      max_qty_per_day: cfg.max_qty_per_day != null ? String(cfg.max_qty_per_day) : '',
    };
  });

  // SAM-546 — parse settings.resources (see ServiceResourceConfig). A legacy resource entry
  // saved without an explicit `key` (shouldn't happen once this UI is the only writer, but
  // defensive against hand-edited data) falls back to slugifying its name, same as the backend.
  const resources: ResourceForm[] = [];
  (service.settings?.resources || []).forEach((r) => {
    const { price, included_pax, max_pax, ...metadataRest } = (r.metadata || {}) as Record<string, unknown>;
    resources.push({
      price: price != null ? String(price) : '',
      included_pax: included_pax != null ? String(included_pax) : '',
      max_pax: max_pax != null ? String(max_pax) : '',
      ...(Object.keys(metadataRest).length ? { metadata_rest: metadataRest } : {}),
      key: r.key || uniqueResourceKey(r.name || 'resource', resources),
      name: r.name || '',
      capacity: r.capacity != null ? String(r.capacity) : '',
      max_simultaneous_bookings: r.max_simultaneous_bookings != null ? String(r.max_simultaneous_bookings) : '',
    });
  });

  // SAM-546 — parse restrictions.availability, "unavailable" periods only (see ServiceClosuresEditor).
  const closures: ClosureForm[] = (service.restrictions?.availability || [])
    .filter((p) => p.status === 'unavailable')
    .map((p) => ({
      start_date: (p.start || '').slice(0, 10),
      end_date: (p.end || '').slice(0, 10),
      reason: p.reason || '',
    }));

  return {
    name_en: service.name_en || service.name || '',
    name_es: service.name_es || '',
    description_en: service.description_en?.[0]?.content || service.description || '',
    description_es: service.description_es?.[0]?.content || '',
    // SAM-1009 — is_active is the flag booking enforces; status/status_id only back it up (isServiceActive).
    status: isServiceActive(service) ? 'active' : 'inactive',
    business_id: service.business_id || businessId || '',
    operating_hours: opHours,
    hours_source: hoursSourceOf(service.settings),
    // SAM-198 follow-up (PR #420) — 'flexible' is now a real, round-trippable
    // booking_type (equipment's default scheduling type); anything else
    // (including an absent/unrecognized service.type) still defaults to
    // 'fixed', unchanged pre-existing behaviour.
    booking_type: service.type === 'day' || service.type === 'flexible' ? service.type : 'fixed',
    // Degrades cleanly when the backend predates SAM-533 (field absent).
    subtype: (service.subtype as ServiceSubtype) || '',
    // SAM-533 — degrades cleanly when the backend predates the group/type
    // columns (fields absent).
    service_group_id: service.service_group_id != null ? String(service.service_group_id) : '',
    service_type_id: service.service_type_id != null ? String(service.service_type_id) : '',
    interval_duration: String(intervals?.duration ? parseISODurationToMinutes(intervals.duration) : (service.duration ? Number(service.duration) : 60)),
    max_per_slot: String(intervals?.max_per_slot ?? 1),
    start_padding: String(intervals?.start_padding ? parseISODurationToMinutes(intervals.start_padding) : 0),
    end_padding: String(intervals?.end_padding ? parseISODurationToMinutes(intervals.end_padding) : 0),
    bookable_interval: intervals?.bookable_interval ? String(parseISODurationToMinutes(intervals.bookable_interval)) : '',
    min_days: service.settings?.min_days != null ? String(service.settings.min_days) : '',
    max_days: service.settings?.max_days != null ? String(service.settings.max_days) : '',
    // SAM-546: fixed to round-trip through ISO-8601 duration, matching what `engine_sync.py`
    // forwards verbatim and what the local read path (`iso8601_to_timedelta`/`iso8601_to_days`)
    // parses — the pre-SAM-546 form read/wrote the raw number, which is not a valid duration.
    booking_window_start: service.booking_window_start ? String(parseISODurationToHours(service.booking_window_start)) : '',
    booking_window_end: service.booking_window_end ? String(parseISODurationToDays(service.booking_window_end)) : '',
    // SAM-546: for a 'day' service, prefer settings.daily_capacity (what the engine actually
    // reads for its simultaneous-bookings limit) over max_capacity, falling back to max_capacity
    // for a service saved before this fix ever wrote daily_capacity.
    max_capacity: service.type === 'day' && service.settings?.daily_capacity != null
      ? String(service.settings.daily_capacity)
      : service.max_capacity != null ? String(service.max_capacity) : '',
    event_size: isEventSize(service.settings?.event_size) ? service.settings.event_size : '',
    badge: isServiceBadge(service.settings?.badge) ? service.settings.badge : '',
    cancellation_time_limit: String(cancellation?.time_limit ? parseISODurationToHours(cancellation.time_limit) : 24),
    cancellation_penalty_fee: cancellation?.penalty_fee ?? false,
    cancellation_penalty_percent: penaltyPercentFromSettings(cancellation?.penalty_percent),
    waitlist_enabled: Boolean(bookingPolicy.waitlist_enabled),
    approval_flow: (bookingPolicy.approval_flow as 'instant' | 'request') || 'instant',
    payment_requirement: (bookingPolicy.payment_requirement as FormData['payment_requirement']) || 'free',
    payment_amount: bookingPolicy.payment_amount != null ? String(bookingPolicy.payment_amount) : '',
    payment_hold_method: (bookingPolicy.payment_hold_method as FormData['payment_hold_method']) || 'cc_auth',
    cancellation_inside_window: (bookingPolicy.cancellation_inside_window as FormData['cancellation_inside_window']) || 'auto',
    cancellation_outside_window: (bookingPolicy.cancellation_outside_window as FormData['cancellation_outside_window']) || 'request',
    age_restriction: bookingPolicy.age_restriction != null ? String(bookingPolicy.age_restriction) : '',
    terms_conditions: typeof bookingPolicy.terms_conditions === 'string' ? bookingPolicy.terms_conditions : '',
    booking_options: options,
    add_ons: addOns,
    pricing_default_user_type_id: service.pricing?.default_user_type_id != null
      ? String(service.pricing.default_user_type_id) : '',
    pricing_roles: pricingRoles,
    tier_groups: tierGroups,
    rate_groups: rateGroupsToForm(service.pricing?.rate_groups),
    seasons: parseSeasons(service.pricing?.seasons),
    // Explicit only: an event subtype saved before SAM-592 (no price_unit) stays per person until
    // the admin picks "Per event size" — loading must never change how an existing service prices.
    price_unit: service.pricing?.price_unit === 'unit' || service.pricing?.price_unit === 'event'
      ? service.pricing.price_unit : 'person',
    unit_price: service.pricing?.unit?.price != null ? String(service.pricing.unit.price) : '',
    unit_included_pax: service.pricing?.unit?.included_pax != null ? String(service.pricing.unit.included_pax) : '',
    unit_max_pax: service.pricing?.unit?.max_pax != null ? String(service.pricing.unit.max_pax) : '',
    resources,
    closures,
    background_img_url: service.background_img_url || '',
    background_color: service.background_color || '#D4D4D4',
  };
}

// SAM-656 — every `settings` key formToServiceData below writes (or deliberately omits to clear).
// Anything else on the loaded service's settings is preserved as-is on save.
export const MANAGED_SETTINGS_KEYS = new Set([
  'intervals', 'cancellation_policy', 'daily_capacity', 'min_days', 'max_days', 'resources',
  'event_size', 'booking_policy', 'add_ons', 'hours_source', 'badge',
]);

export function unmanagedSettings(settings: unknown): Record<string, unknown> {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) return {};
  return Object.fromEntries(Object.entries(settings as Record<string, unknown>)
    .filter(([key]) => !MANAGED_SETTINGS_KEYS.has(key)));
}

// SAM-466 — the cancellation_policy keys formToServiceData writes; any other key is preserved.
const MANAGED_CANCELLATION_KEYS = new Set(['time_limit', 'penalty_fee', 'penalty_percent']);

export function unmanagedCancellationPolicy(policy: unknown): Record<string, unknown> {
  if (!policy || typeof policy !== 'object' || Array.isArray(policy)) return {};
  return Object.fromEntries(Object.entries(policy as Record<string, unknown>)
    .filter(([key]) => !MANAGED_CANCELLATION_KEYS.has(key)));
}

export function operatingHoursWereLoadedOrEdited(
  formData: FormData, mode: 'create' | 'edit', existingService: Service | null,
): boolean {
  if (mode === 'create' || !existingService) return true;
  if (existingService.operating_hours != null) return true;
  const loaded = serviceToForm(existingService, null).operating_hours;
  return JSON.stringify(formData.operating_hours) !== JSON.stringify(loaded);
}

export function formToServiceData(formData: FormData, mode: 'create' | 'edit', existingService: Service | null): Partial<Service> {
  // Build operating_hours (only enabled days). SAM-198 — windows are
  // reordered so the earliest becomes open/close and the rest (sorted)
  // become extra_windows, EXCEPT when the first window crosses midnight
  // (e.g. a bar open 18:00 -> 02:00): that's still the correct anchor
  // window even though its clock time isn't the day's earliest, so it's
  // left in place and only the extras are sorted. The key is omitted
  // entirely when there are no extras, so a service with none produces the
  // exact same payload it always did.
  const operating_hours: OperatingHours = {};
  Object.entries(formData.operating_hours).forEach(([day, config]) => {
    if (config.enabled && config.open && config.close) {
      const { first, extras } = buildDayWindowsForSave(
        {
          open: config.open,
          close: config.close,
          label: config.label,
          capacity: config.capacity,
          instructor: config.instructor,
        },
        config.extra_windows
      );
      // SAM-546 — strip empty label/capacity/instructor from each extra so an
      // untouched session's payload stays byte-for-byte what it was before
      // this field existed (only a value the admin actually typed is sent).
      const cleanedExtras = extras.map(({ open, close, label, capacity, instructor }) => ({
        open,
        close,
        ...(label ? { label } : {}),
        ...(capacity ? { capacity } : {}),
        ...(instructor ? { instructor } : {}),
      }));

      operating_hours[day] = {
        open: first.open,
        close: first.close,
        ...(cleanedExtras.length ? { extra_windows: cleanedExtras } : {}),
        // Same byte-for-byte guarantee for the day's first/base window.
        ...(first.label ? { label: first.label } : {}),
        ...(first.capacity ? { capacity: first.capacity } : {}),
        ...(first.instructor ? { instructor: first.instructor } : {}),
      };
    }
  });

  // SAM-546 (N11) — settings.resources, only sent when the admin actually added rows. An empty
  // array behaves identically to omitting the field on the backend (`_normalize_resource_configs`
  // falls back to the single "default" resource for both), but omitting it entirely keeps a
  // plain service's payload byte-for-byte what it always was pre-SAM-546.
  const resources: ServiceResourceConfig[] | undefined = formData.resources.length
    ? formData.resources
        .filter((r) => r.name.trim())
        .map((r) => ({
          key: r.key,
          name: r.name.trim(),
          ...(r.capacity !== '' ? { capacity: Number(r.capacity) || 0 } : {}),
          ...(r.max_simultaneous_bookings !== '' ? { max_simultaneous_bookings: Number(r.max_simultaneous_bookings) || 1 } : {}),
          // SAM-591 — per-unit override of pricing.unit, only meaningful (and only written) for a
          // unit-priced service. Blank = inherit the service default. Unknown metadata keys are kept.
          ...buildResourceMetadata(r, formData.price_unit === 'unit'),
        }))
    : undefined;

  // SAM-590 — Add-Ons. Computed ahead of `settings` so it can be spread into it below. Omitted
  // from settings entirely when there are none, so a service with no add-ons keeps sending
  // exactly the pre-SAM-590 settings shape (byte-identical payload).
  const add_ons: ServiceAddOn[] = formData.add_ons
    .filter((a) => a.label_en.trim())
    .map((a) => {
      const prices_by_role: Record<string, number> = {};
      Object.entries(a.prices_by_role || {}).forEach(([roleId, value]) => {
        if (value !== '' && value != null) prices_by_role[roleId] = Number(value) || 0;
      });
      return {
        id: a.id || slugifyAddOnId(a.label_en) || `addon_${Math.random().toString(36).slice(2, 8)}`,
        label_en: a.label_en,
        label_es: a.label_es,
        price: Number(a.price) || 0,
        currency: a.currency || 'USD',
        max_qty: Number(a.max_qty) || 1,
        ...(a.required ? { required: true } : {}),
        ...(Object.keys(prices_by_role).length ? { prices_by_role } : {}),
      };
    });

  // SAM-592 — events are priced by size band, not per ticket.
  const isEventService = formData.price_unit === 'event';
  const eventSize: EventSize | null = isEventSize(formData.event_size)
    ? formData.event_size
    : suggestEventSize(formData.max_capacity);

  // Build settings. 'day' services don't use a slot duration/max_per_slot —
  // they keep the field so nothing is lost if the type is switched back,
  // but scheduling for 'day' resolves from operating_hours instead (SAM-196).
  const settings = {
    // SAM-656 — keys this editor does not manage (e.g. a day service's start_time/end_time) are
    // carried over from the loaded service instead of being dropped on save.
    ...unmanagedSettings(existingService?.settings),
    intervals: {
      duration: minutesToISODuration(Number(formData.interval_duration) || 60),
      max_per_slot: Number(formData.max_per_slot) || 1,
      start_padding: minutesToISODuration(Number(formData.start_padding) || 0),
      end_padding: minutesToISODuration(Number(formData.end_padding) || 0),
      // SAM-198 field-parity pass — optional, 'fixed' only. Omitted rather than sent as "once
      // per duration" so a service that never set it keeps its pre-existing payload shape.
      ...(formData.booking_type === 'fixed' && formData.bookable_interval
        ? { bookable_interval: minutesToISODuration(Number(formData.bookable_interval) || 0) }
        : {}),
    },
    cancellation_policy: {
      // SAM-466 — keys this editor does not manage inside the policy are carried over.
      ...unmanagedCancellationPolicy(existingService?.settings?.cancellation_policy),
      time_limit: hoursToISODuration(Number(formData.cancellation_time_limit) || 24),
      // NOTE: has no engine counterpart (functions/booking/engine_sync.py never reads it) —
      // SAM-side/manual enforcement only. See the caption on this field below.
      penalty_fee: formData.cancellation_penalty_fee,
      // SAM-466 — omitted when empty: the backend reads a missing percent as "no deduction".
      ...(penaltyPercentForSave(formData.cancellation_penalty_percent) !== undefined
        ? { penalty_percent: penaltyPercentForSave(formData.cancellation_penalty_percent) }
        : {}),
    },
    // SAM-546 fix: 'day' services (cabana/minconsumo) never wrote `settings.daily_capacity`
    // before this change — only the top-level `max_capacity` column, which `engine_sync.py`'s
    // `_default_max_simultaneous('day', ...)` does NOT read (it reads `settings.daily_capacity`
    // specifically). Without this, a day-type service's "Daily Capacity" field silently had no
    // effect on the engine's simultaneous-bookings limit — exactly the kind of admin field with
    // no destination this ticket's acceptance criteria rules out.
    ...(formData.booking_type === 'day' && formData.max_capacity
      ? { daily_capacity: Number(formData.max_capacity) || undefined }
      : {}),
    // SAM-198 field-parity pass — 'day' only, optional. Read by
    // `engine_sync.py::_build_service_payload` as `min_days`/`max_days`; no local read path
    // today (SAM-198 backend research, 2026-09-23), so this only affects the synced engine copy.
    ...(formData.booking_type === 'day' && formData.min_days ? { min_days: Number(formData.min_days) || undefined } : {}),
    ...(formData.booking_type === 'day' && formData.max_days ? { max_days: Number(formData.max_days) || undefined } : {}),
    ...(resources ? { resources } : {}),
    // SAM-592 — event size band, events only. No column for it: lives in settings (jsonb) next to the
    // other taxonomy-ish fields. Pricing does not trust it (the backend derives the band from the
    // booking's pax); it is the admin-confirmed size shown back in the editor.
    ...(isEventService && eventSize ? { event_size: eventSize } : {}),
    // SAM-198 Policies parity — stored as-is for round-tripping the admin's edits, but nothing in
    // sam-lambda-functions reads settings.booking_policy today (see the FormData comment). Kept
    // out of ENGINE_SCHEDULING_FIELD_PATHS on purpose — that constant is a parity contract with
    // the engine sync, and none of this block is.
    booking_policy: {
      waitlist_enabled: formData.waitlist_enabled,
      approval_flow: formData.approval_flow,
      payment_requirement: formData.payment_requirement,
      ...(formData.payment_amount ? { payment_amount: Number(formData.payment_amount) || formData.payment_amount } : {}),
      ...(formData.payment_requirement === 'cc_hold' ? { payment_hold_method: formData.payment_hold_method } : {}),
      cancellation_inside_window: formData.cancellation_inside_window,
      cancellation_outside_window: formData.cancellation_outside_window,
      ...(formData.age_restriction ? { age_restriction: Number(formData.age_restriction) || formData.age_restriction } : {}),
      ...(formData.terms_conditions ? { terms_conditions: formData.terms_conditions } : {}),
    },
    ...(add_ons.length ? { add_ons } : {}),
    // SAM-656 — where the hours come from ('business' | 'custom').
    ...hoursSourceForSave(formData.hours_source, existingService?.settings),
    // SAM-657 — the badge key, only when the admin picked one ("Suggested" stores nothing).
    ...badgeForSave(formData.badge),
  };

  // SAM-588 — rate groups first: the booking options' price cells are pruned against their ids.
  const rateGroups = rateGroupsToPayload(formData.rate_groups);
  const rateGroupIds = new Set(rateGroups.map((g) => g.id));

  // Build booking_options
  const booking_options: BookingOption[] = formData.booking_options
    .filter((opt) => opt.label_en.trim())
    .map((opt, index) => {
      const prices_by_role: Record<string, number> = {};
      Object.entries(opt.prices_by_role || {}).forEach(([roleId, value]) => {
        if (value !== '' && value != null) prices_by_role[roleId] = Number(value) || 0;
      });
      const prices_by_group = pricesByGroupToPayload(opt.prices_by_group, rateGroupIds);
      const prices_by_event_size: Record<string, number> = {};
      if (isEventService) {
        EVENT_SIZE_BANDS.forEach(({ key }) => {
          const value = opt.prices_by_event_size?.[key];
          if (value !== '' && value != null) prices_by_event_size[key] = Number(value) || 0;
        });
      }
      return {
        id: opt.id || `option_${index + 1}`,
        label_en: opt.label_en,
        label_es: opt.label_es,
        short_desc_en: opt.short_desc_en,
        short_desc_es: opt.short_desc_es,
        price: Number(opt.price) || 0,
        currency: opt.currency || 'USD',
        min_amount: Number(opt.min_amount) || 0,
        max_amount: Number(opt.max_amount) || 1,
        ...(Object.keys(prices_by_role).length ? { prices_by_role } : {}),
        ...(Object.keys(prices_by_group).length ? { prices_by_group } : {}),
        // SAM-591 — kept only for a unit-priced service; blank is omitted, never sent as 0 (an
        // extra guest for free is a real config, an unset one must be rejected by the backend).
        ...(formData.price_unit === 'unit' && (opt.extra_guest_price ?? '').trim() !== ''
          ? { extra_guest_price: Number(opt.extra_guest_price) || 0 } : {}),
        ...(Object.keys(prices_by_event_size).length ? { prices_by_event_size } : {}),
      };
    });

  // Build pricing config (SAM-195) — omitted entirely when the service has
  // no role prices/caps set, so a plain service keeps sending exactly the
  // SAM-193 shape it always did.
  const pricingRoles: Record<string, { max_qty_per_booking?: number; max_qty_per_day?: number }> = {};
  Object.entries(formData.pricing_roles).forEach(([roleId, caps]) => {
    const entry: { max_qty_per_booking?: number; max_qty_per_day?: number } = {};
    if (caps.max_qty_per_booking !== '') entry.max_qty_per_booking = Number(caps.max_qty_per_booking);
    if (caps.max_qty_per_day !== '') entry.max_qty_per_day = Number(caps.max_qty_per_day);
    if (Object.keys(entry).length) pricingRoles[roleId] = entry;
  });
  // Tier groups (SAM-195 follow-up). Only groups with an id AND at least one option are sent: a
  // half-filled row in the editor is not a rule, and pricing.py identifies a group by its id when
  // it rejects a booking. Blank caps are omitted rather than sent as 0, which would mean "nothing
  // may be booked" instead of "no cap".
  const tierGroups = formData.tier_groups
    .filter((group) => group.id.trim() && group.tiers.length > 0)
    .map((group) => {
      const label: { es?: string; en?: string } = {};
      if (group.label_es.trim()) label.es = group.label_es.trim();
      if (group.label_en.trim()) label.en = group.label_en.trim();
      return {
        id: group.id.trim(),
        ...(Object.keys(label).length ? { label } : {}),
        tiers: [...group.tiers],
        ...(group.max_total.trim() !== '' ? { max_total: Number(group.max_total) } : {}),
        ...(group.included.trim() !== '' ? { included: Number(group.included) } : {}),
      };
    });

  const seasons = serializeSeasons(formData.seasons, booking_options.map((o) => o.id).filter(Boolean));

  const hasRolePrices = booking_options.some((opt) => opt.prices_by_role && Object.keys(opt.prices_by_role).length);
  const unitPricing: ServicePricing['unit'] = {
    ...(formData.unit_price.trim() !== '' ? { price: Number(formData.unit_price) || 0 } : {}),
    ...(formData.unit_included_pax.trim() !== '' ? { included_pax: Number(formData.unit_included_pax) || 0 } : {}),
    ...(formData.unit_max_pax.trim() !== '' ? { max_pax: Number(formData.unit_max_pax) || 0 } : {}),
  };
  const pricing: ServicePricing | undefined = (formData.price_unit !== 'person' || hasRolePrices || formData.pricing_default_user_type_id || Object.keys(pricingRoles).length || tierGroups.length || rateGroups.length || seasons.length)
    ? {
        ...(formData.price_unit === 'unit' ? { price_unit: 'unit' as const, unit: unitPricing } : {}),
        ...(isEventService ? { price_unit: 'event' as const } : {}),
        ...(formData.pricing_default_user_type_id ? { default_user_type_id: Number(formData.pricing_default_user_type_id) } : {}),
        ...(Object.keys(pricingRoles).length ? { roles: pricingRoles } : {}),
        ...(tierGroups.length ? { tier_groups: tierGroups } : {}),
        ...(rateGroups.length ? { rate_groups: rateGroups } : {}),
        ...(seasons.length ? { seasons } : {}),
      }
    : undefined;

  // SAM-546 — restrictions.availability. Preserve any period this editor doesn't manage (anything
  // whose status isn't "unavailable") from the previously-loaded service, so an edit here never
  // drops unrelated restriction data — "las reglas... se guardan y recargan sin pérdida".
  const preservedPeriods: ServiceAvailabilityPeriod[] = (existingService?.restrictions?.availability || [])
    .filter((p) => p.status !== 'unavailable');
  const closurePeriods: ServiceAvailabilityPeriod[] = formData.closures
    .filter((c) => c.start_date && c.end_date && c.end_date >= c.start_date)
    .map((c) => ({
      start: c.start_date,
      end: c.end_date,
      status: 'unavailable',
      ...(c.reason.trim() ? { reason: c.reason.trim() } : {}),
    }));
  const availability = [...preservedPeriods, ...closurePeriods];
  const restrictions = availability.length ? { ...existingService?.restrictions, availability } : undefined;

  // Map status string to status_id (DB uses integer FK)
  const statusMap: Record<string, number> = { active: 1, inactive: 2 };

  // Build description JSONB arrays
  const description_en: DescriptionBlock[] = formData.description_en.trim()
    ? [{ order: 1, title: '', content: formData.description_en.trim() }]
    : [];
  const description_es: DescriptionBlock[] = formData.description_es.trim()
    ? [{ order: 1, title: '', content: formData.description_es.trim() }]
    : [];

  // SAM-533/SAM-545 — subtype and type (scheduling) always travel together so
  // crud's `validate_table_business_rules` (which only cross-checks the two
  // when BOTH are present on the same request) never sees a partial update
  // that could drift them apart. When a subtype is set, `type` is derived
  // from it — the UI already locks Booking Type to match (see
  // updateSubtype) — so this is a defensive re-derivation, not a UI-vs-save
  // discrepancy.
  const subtype = formData.subtype || undefined;
  const bookingType = subtype ? SERVICE_SUBTYPE_SCHEDULING_TYPE[subtype] : formData.booking_type;

  const data: Partial<Service> = {
    name_en: formData.name_en || undefined,
    name_es: formData.name_es || undefined,
    description_en: description_en.length ? description_en : undefined,
    description_es: description_es.length ? description_es : undefined,
    type: bookingType,
    subtype: subtype ?? null,
    // SAM-533 — service_group_id / service_type_id are nullable FKs; an
    // empty select clears a previously-set value (unlike the `|| undefined`
    // fields above, which simply omit themselves), matching how the group
    // and type selects have an explicit "unset" option.
    service_group_id: formData.service_group_id ? Number(formData.service_group_id) : null,
    service_type_id: formData.service_type_id ? Number(formData.service_type_id) : null,
    // SAM-656 — a service loaded WITHOUT operating_hours (a /services/description that predates the
    // field) showed every day as closed; sending that back would wipe the stored hours. Only send
    // them when they were loaded, or when the admin actually edited them in this session.
    ...(operatingHoursWereLoadedOrEdited(formData, mode, existingService) ? { operating_hours } : {}),
    restrictions,
    settings,
    booking_options,
    // Edit of a service that HAD pricing and now needs none (e.g. switched back to "per person"
    // with no roles/groups/seasons): send an explicit null, otherwise the omitted key would leave
    // the saved pricing — including price_unit "unit"/"event" — live. A service that never had
    // pricing keeps omitting the key (byte-identical payload for an untouched plain service).
    pricing: pricing ?? (mode === 'edit' && existingService?.pricing ? null : undefined),
    // SAM-546: serialized as ISO-8601 durations now (see the serviceToForm comment above) —
    // previously sent the raw number, which `engine_sync.py` and the local read path both expect
    // to already be a duration string.
    booking_window_start: formData.booking_window_start ? hoursToISODuration(Number(formData.booking_window_start) || 0) : undefined,
    booking_window_end: formData.booking_window_end ? daysToISODuration(Number(formData.booking_window_end) || 0) : undefined,
    max_capacity: formData.max_capacity ? Number(formData.max_capacity) : undefined,
    // SAM-1009 — Service Image is the cover (background_img_url); on edit, emptying it clears the stored value.
    background_img_url: formData.background_img_url || (mode === 'edit' && existingService?.background_img_url ? null : undefined),
    background_color: formData.background_color || undefined,
    duration: formData.interval_duration || undefined,
  };

  // status_id is an integer FK in the DB, not a string
  if (formData.status) {
    (data as any).status_id = statusMap[formData.status] ?? 1;
    // SAM-1009 — sam_core.service.is_active defaults to false and booking refuses inactive services,
    // so the Active/Inactive switch has to write it too.
    data.is_active = formData.status === 'active';
  }

  if (mode === 'create') {
    data.business_id = formData.business_id || undefined;
  }

  return data;
}

/**
 * The id of the service a POST /crud/service created. The lambda answers `{"data":[{row}],"statusCode":200}`
 * (SAM-1009, QA 21 oct), older shapes were `{data:{id}}` and `{id}`; the response is the axios one (`.data` is the body).
 */
export function createdServiceId(response: unknown): string | undefined {
  const body = (response as { data?: unknown } | undefined)?.data as { data?: unknown; id?: unknown } | unknown[] | undefined;
  const inner = Array.isArray(body) ? body : (body as { data?: unknown } | undefined)?.data;
  const row = (Array.isArray(inner) ? inner[0] : inner) as { id?: unknown } | undefined;
  const id = row?.id ?? (Array.isArray(body) ? undefined : (body as { id?: unknown } | undefined)?.id);
  return id == null || id === '' ? undefined : String(id);
}

// --- Stateful hook (shared by the drawer during transition and the new page) ---

export interface UseServiceEditorArgs {
  mode: 'create' | 'edit';
  service: Service | null;
  businessId: string | null;
  /** Re-runs the reset-from-service effect (e.g. drawer open/close, page mount). */
  resetKey?: unknown;
}

export function useServiceEditor({ mode, service, businessId, resetKey }: UseServiceEditorArgs) {
  const createService = useCreateService();
  const updateService = useUpdateService();
  const deleteService = useDeleteService();
  const syncServiceToEngine = useSyncServiceToEngine();

  const [formData, setFormData] = useState<FormData>(buildInitialForm(businessId));
  // Snapshot of the form exactly as loaded (or as freshly initialized for 'create'), used to
  // derive the unsaved-changes ("dirty") state for the sticky save bar and the navigation guard.
  const [initialSnapshot, setInitialSnapshot] = useState<string>(() => JSON.stringify(buildInitialForm(businessId)));
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basic: true,
    operatingHours: true,
    resources: false,
    closures: false,
    preview: false,
    bookingConfig: true,
    bookingOptions: true,
    rolePricing: false,
    cancellation: false,
    media: false,
  });

  useEffect(() => {
    if (mode === 'edit' && service) {
      const next = serviceToForm(service, businessId);
      setFormData(next);
      setInitialSnapshot(JSON.stringify(next));
      // Progressive disclosure (SAM-195): only auto-expand when the service
      // already has more than one role priced — otherwise stay collapsed.
      const roleIds = new Set<string>(Object.keys(next.pricing_roles));
      next.booking_options.forEach((opt) => Object.keys(opt.prices_by_role).forEach((r) => roleIds.add(r)));
      setExpandedSections((prev) => ({ ...prev, rolePricing: roleIds.size > 1 }));
    } else {
      const next = buildInitialForm(businessId);
      setFormData(next);
      setInitialSnapshot(JSON.stringify(next));
    }
    setShowDeleteConfirm(false);
  }, [service, mode, businessId, resetKey]);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // SAM-591 — switching the pricing model. Moving to "Per unit" carries an old per-person cabana
  // over (its min = max = 1 rental line becomes the unit price); `unitCarryOver` names that line
  // so the section can say what moved. Nothing is saved until the admin saves.
  // SAM-626 — season problem reported by the server on the last save attempt.
  // SAM-657 — the CRUD 400 `invalid_service_badge`, shown on the Badge field.
  const [badgeServerIssue, setBadgeServerIssue] = useState<string | null>(null);
  const [seasonServerIssue, setSeasonServerIssue] = useState<SeasonSaveIssue | null>(null);
  // SAM-1009 — any other rejected save (e.g. 400 "Data violates check constraint"): the API's own text,
  // kept in the save bar until the next save or edit (the toast alone disappears in 3 s).
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => { setSaveError(null); }, [formData]);
  // Editing the seasons retires the server's verdict (it was about the payload that was sent).
  useEffect(() => { setSeasonServerIssue(null); }, [formData.seasons]);
  const [unitCarryOver, setUnitCarryOver] = useState<string | null>(null);
  const updatePriceUnit = (price_unit: PriceUnit) => {
    const carried = price_unit === 'unit' && formData.price_unit !== 'unit'
      ? carryOverToUnit(formData.unit_price, formData.booking_options) : null;
    setUnitCarryOver(carried?.moved ?? null);
    setFormData((prev) => (carried
      ? { ...prev, price_unit, unit_price: carried.unit_price, booking_options: carried.booking_options }
      : { ...prev, price_unit }));
  };

  // SAM-545 — picking a subtype locks Booking Type to the scheduling type
  // the backend requires for it (see SERVICE_SUBTYPE_SCHEDULING_TYPE), so the
  // wizard's own state can never disagree with what gets sent to crud.
  const updateSubtype = (subtype: '' | ServiceSubtype) => {
    setFormData((prev) => ({
      ...prev,
      subtype,
      booking_type: subtype ? SERVICE_SUBTYPE_SCHEDULING_TYPE[subtype] : prev.booking_type,
    }));
  };

  const updateOperatingHour = (day: string, field: keyof OperatingHourForm, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: { ...prev.operating_hours[day], [field]: value },
      },
    }));
  };

  // --- Operating hours: extra windows (SAM-198) ---

  const addExtraWindow = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          extra_windows: [
            ...prev.operating_hours[day].extra_windows,
            { open: '', close: '', label: '', capacity: '', instructor: '' },
          ],
        },
      },
    }));
  };

  const removeExtraWindow = (day: string, index: number) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          extra_windows: prev.operating_hours[day].extra_windows.filter((_, i) => i !== index),
        },
      },
    }));
  };

  const updateExtraWindow = (
    day: string,
    index: number,
    field: 'open' | 'close' | 'label' | 'capacity' | 'instructor',
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          extra_windows: prev.operating_hours[day].extra_windows.map((w, i) =>
            i === index ? { ...w, [field]: value } : w
          ),
        },
      },
    }));
  };

  // --- Resources (SAM-546 / N11) ---

  const addResource = () => {
    setFormData((prev) => {
      const name = `Resource ${prev.resources.length + 1}`;
      return {
        ...prev,
        resources: [...prev.resources, {
          key: uniqueResourceKey(name, prev.resources),
          name,
          capacity: '',
          max_simultaneous_bookings: '',
        }],
      };
    });
  };

  const removeResource = (index: number) => {
    setFormData((prev) => ({ ...prev, resources: prev.resources.filter((_, i) => i !== index) }));
  };

  const updateResource = (index: number, field: ResourceFormField, value: string) => {
    setFormData((prev) => ({
      ...prev,
      resources: prev.resources.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    }));
  };

  // --- Closures / exceptions (SAM-546 / N11) ---

  const addClosure = () => {
    setFormData((prev) => ({
      ...prev,
      closures: [...prev.closures, { start_date: '', end_date: '', reason: '' }],
    }));
  };

  const removeClosure = (index: number) => {
    setFormData((prev) => ({ ...prev, closures: prev.closures.filter((_, i) => i !== index) }));
  };

  const updateClosure = (index: number, field: keyof ClosureForm, value: string) => {
    setFormData((prev) => ({
      ...prev,
      closures: prev.closures.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    }));
  };

  const addTierGroup = () => {
    setFormData((prev) => ({
      ...prev,
      tier_groups: [
        ...prev.tier_groups,
        { id: '', label_es: '', label_en: '', tiers: [], max_total: '', included: '' },
      ],
    }));
  };

  const removeTierGroup = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      tier_groups: prev.tier_groups.filter((_, i) => i !== index),
    }));
  };

  const updateTierGroup = (
    index: number,
    field: keyof Omit<TierGroupForm, 'tiers'>,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      tier_groups: prev.tier_groups.map((g, i) => (i === index ? { ...g, [field]: value } : g)),
    }));
  };

  const toggleTierGroupOption = (index: number, optionId: string) => {
    setFormData((prev) => ({
      ...prev,
      tier_groups: prev.tier_groups.map((g, i) => {
        if (i !== index) return g;
        // Appending rather than re-sorting keeps the order meaningful: pricing.py hands out the
        // `included` free places following the order of `tiers`.
        const tiers = g.tiers.includes(optionId)
          ? g.tiers.filter((t) => t !== optionId)
          : [...g.tiers, optionId];
        return { ...g, tiers };
      }),
    }));
  };

  const addBookingOption = () => {
    setFormData((prev) => ({
      ...prev,
      booking_options: [...prev.booking_options, { ...EMPTY_OPTION, id: `option_${prev.booking_options.length + 1}` }],
    }));
  };

  const removeBookingOption = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      booking_options: prev.booking_options.filter((_, i) => i !== index),
    }));
  };

  const updateBookingOption = (index: number, field: keyof BookingOptionForm, value: string) => {
    setFormData((prev) => ({
      ...prev,
      booking_options: prev.booking_options.map((opt, i) =>
        i === index ? { ...opt, [field]: value } : opt
      ),
    }));
  };

  // --- Add-Ons (SAM-590) ---

  const addAddOn = () => {
    setFormData((prev) => {
      // Default currency = the service's first booking option currency, per the ticket, falling
      // back to EMPTY_ADD_ON's 'USD' when there are no booking options yet.
      const currency = prev.booking_options[0]?.currency || EMPTY_ADD_ON.currency;
      return { ...prev, add_ons: [...prev.add_ons, { ...EMPTY_ADD_ON, currency }] };
    });
  };

  const removeAddOn = (index: number) => {
    setFormData((prev) => ({ ...prev, add_ons: prev.add_ons.filter((_, i) => i !== index) }));
  };

  const updateAddOn = (index: number, field: keyof AddOnForm, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      add_ons: prev.add_ons.map((addOn, i) => {
        if (i !== index) return addOn;
        const next = { ...addOn, [field]: value };
        // Auto-slug the id from the label as the admin types, but only while they haven't
        // deliberately typed their own id — once `id` diverges from the label's own slug, the
        // admin owns it and this stops touching it.
        if (field === 'label_en' && (addOn.id === '' || addOn.id === slugifyAddOnId(addOn.label_en))) {
          next.id = slugifyAddOnId(String(value));
        }
        return next;
      }),
    }));
  };

  const reorderAddOn = (index: number, direction: -1 | 1) => {
    setFormData((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.add_ons.length) return prev;
      const add_ons = [...prev.add_ons];
      [add_ons[index], add_ons[target]] = [add_ons[target], add_ons[index]];
      return { ...prev, add_ons };
    });
  };

  const updateAddOnRolePrice = (index: number, userTypeId: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      add_ons: prev.add_ons.map((addOn, i) =>
        i === index ? { ...addOn, prices_by_role: { ...addOn.prices_by_role, [userTypeId]: value } } : addOn
      ),
    }));
  };

  // SAM-590 validation — price >= 0, max_qty >= 1, unique ids (blank labels are dropped on save,
  // not validated here — an in-progress empty row isn't an error yet).
  const addOnErrors: Record<number, string[]> = {};
  {
    const seenIds = new Map<string, number>();
    formData.add_ons.forEach((addOn, i) => {
      if (!addOn.label_en.trim()) return;
      const problems: string[] = [];
      const price = Number(addOn.price);
      if (addOn.price !== '' && (!Number.isFinite(price) || price < 0)) problems.push('Price must be 0 or more.');
      const maxQty = Number(addOn.max_qty);
      if (!Number.isFinite(maxQty) || maxQty < 1) problems.push('Max qty must be at least 1.');
      const id = addOn.id || slugifyAddOnId(addOn.label_en);
      if (id && seenIds.has(id)) {
        problems.push(`Id "${id}" is already used by another add-on.`);
        addOnErrors[seenIds.get(id)!] = [...(addOnErrors[seenIds.get(id)!] || []), `Id "${id}" is already used by another add-on.`];
      } else if (id) {
        seenIds.set(id, i);
      }
      if (problems.length) addOnErrors[i] = [...(addOnErrors[i] || []), ...problems];
    });
  }
  const hasAddOnErrors = Object.keys(addOnErrors).length > 0;

  // --- Rate groups (SAM-588) ---

  const addRateGroup = () => {
    setFormData((prev) => ({
      ...prev,
      // The first group of a service is its default; the admin can move the flag afterwards.
      rate_groups: [...prev.rate_groups, { ...emptyRateGroup(), is_default: prev.rate_groups.length === 0 }],
    }));
  };

  const removeRateGroup = (index: number) => {
    setFormData((prev) => {
      const removed = prev.rate_groups[index];
      const rate_groups = prev.rate_groups.filter((_, i) => i !== index);
      const removedId = removed?.id.trim();
      return {
        ...prev,
        rate_groups,
        // SAM-624: and so do the cells the seasons override for it.
        seasons: removedId ? removeGroupFromSeasons(prev.seasons, removedId) : prev.seasons,
        // Its price cells go with it; they would be pruned on save anyway, but a stale cell would
        // resurrect if a new group re-used the same id.
        booking_options: removedId
          ? prev.booking_options.map((opt) => {
              const prices_by_group = { ...opt.prices_by_group };
              delete prices_by_group[removedId];
              return { ...opt, prices_by_group };
            })
          : prev.booking_options,
      };
    });
  };

  const updateRateGroup = (index: number, patch: Partial<RateGroupForm>) => {
    setFormData((prev) => {
      const current = prev.rate_groups[index];
      if (!current) return prev;
      const next = { ...current, ...patch };
      let booking_options = prev.booking_options;
      let seasons = prev.seasons;
      // Renaming an id moves its price cells (base and season) with it, so a typo fix does not
      // empty the column.
      const oldId = current.id.trim();
      const newId = next.id.trim();
      if (patch.id !== undefined && oldId && oldId !== newId) {
        booking_options = prev.booking_options.map((opt) => {
          if (!(oldId in opt.prices_by_group)) return opt;
          const prices_by_group = { ...opt.prices_by_group };
          const value = prices_by_group[oldId];
          delete prices_by_group[oldId];
          if (newId) prices_by_group[newId] = value;
          return { ...opt, prices_by_group };
        });
        seasons = renameGroupInSeasons(prev.seasons, oldId, newId);
      }
      return {
        ...prev,
        booking_options,
        seasons,
        rate_groups: prev.rate_groups.map((g, i) => {
          if (i === index) return next;
          // Exactly one default: flagging one clears the others.
          return patch.is_default ? { ...g, is_default: false } : g;
        }),
      };
    });
  };

  const updateOptionGroupPrice = (optionIndex: number, groupId: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      booking_options: prev.booking_options.map((opt, i) =>
        i === optionIndex ? { ...opt, prices_by_group: { ...opt.prices_by_group, [groupId]: value } } : opt
      ),
    }));
  };

  // --- Event size pricing (SAM-592) ---
  const updateOptionEventPrice = (optionIndex: number, size: EventSize, value: string) => {
    setFormData((prev) => ({
      ...prev,
      booking_options: prev.booking_options.map((opt, i) =>
        i === optionIndex ? { ...opt, prices_by_event_size: { ...opt.prices_by_event_size, [size]: value } } : opt
      ),
    }));
  };

  // --- Seasons (SAM-589) ---

  const addSeason = () => {
    setFormData((prev) => ({
      ...prev,
      seasons: [...prev.seasons, {
        id: nextSeasonId(prev.seasons), label_en: '', label_es: '', start_date: '', end_date: '', prices: {},
      }],
    }));
  };

  // SAM-626 — flip a season between fixed dates and "every year"; the bounds are converted, not blanked.
  const updateSeasonEveryYear = (index: number, on: boolean) => {
    setFormData((prev) => ({
      ...prev,
      seasons: prev.seasons.map((s, i) => (i === index ? setSeasonEveryYear(s, on) : s)),
    }));
  };

  const removeSeason = (index: number) => {
    setFormData((prev) => ({ ...prev, seasons: prev.seasons.filter((_, i) => i !== index) }));
  };

  const updateSeason = (index: number, field: 'label_en' | 'label_es' | 'start_date' | 'end_date', value: string) => {
    setFormData((prev) => ({
      ...prev,
      seasons: prev.seasons.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    }));
  };

  const updateSeasonPrice = (index: number, optionId: string, field: SeasonPriceField, key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      seasons: prev.seasons.map((s, i) => {
        if (i !== index) return s;
        const entry = s.prices[optionId] || emptySeasonPrice();
        const next = SEASON_SCALAR_FIELDS.includes(field)
          ? { ...entry, [field]: value }
          : { ...entry, [field]: { ...(entry[field] as Record<string, string> | undefined), [key]: value } };
        return { ...s, prices: { ...s.prices, [optionId]: next } };
      }),
    }));
  };

  // --- Role pricing (SAM-195) ---

  const activeRoleIds = React.useMemo(() => {
    const ids = new Set<string>(Object.keys(formData.pricing_roles));
    formData.booking_options.forEach((opt) => Object.keys(opt.prices_by_role).forEach((r) => ids.add(r)));
    return Array.from(ids);
  }, [formData.pricing_roles, formData.booking_options]);

  const updateOptionRolePrice = (optionIndex: number, userTypeId: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      booking_options: prev.booking_options.map((opt, i) =>
        i === optionIndex ? { ...opt, prices_by_role: { ...opt.prices_by_role, [userTypeId]: value } } : opt
      ),
    }));
  };

  const addRoleColumn = (userTypeId: string) => {
    setFormData((prev) => {
      // "Crear un rol nuevo... agrega la columna heredando el precio de
      // Visitor" (ticket) — roles are never hardcoded by id/name, so this
      // seeds each row from the current default role's cell (or the flat
      // price when there is no default set yet), the closest role-agnostic
      // equivalent.
      const booking_options = prev.booking_options.map((opt) => {
        const seed = prev.pricing_default_user_type_id
          ? opt.prices_by_role[prev.pricing_default_user_type_id] ?? opt.price
          : opt.price;
        return { ...opt, prices_by_role: { ...opt.prices_by_role, [userTypeId]: seed } };
      });
      return {
        ...prev,
        booking_options,
        pricing_roles: prev.pricing_roles[userTypeId]
          ? prev.pricing_roles
          : { ...prev.pricing_roles, [userTypeId]: { max_qty_per_booking: '', max_qty_per_day: '' } },
      };
    });
  };

  const removeRoleColumn = (userTypeId: string) => {
    setFormData((prev) => {
      const pricing_roles = { ...prev.pricing_roles };
      delete pricing_roles[userTypeId];
      const booking_options = prev.booking_options.map((opt) => {
        const prices_by_role = { ...opt.prices_by_role };
        delete prices_by_role[userTypeId];
        return { ...opt, prices_by_role };
      });
      return {
        ...prev,
        booking_options,
        pricing_roles,
        pricing_default_user_type_id: prev.pricing_default_user_type_id === userTypeId ? '' : prev.pricing_default_user_type_id,
      };
    });
  };

  const updateRoleCap = (userTypeId: string, field: keyof RoleCapsForm, value: string) => {
    setFormData((prev) => ({
      ...prev,
      pricing_roles: {
        ...prev.pricing_roles,
        [userTypeId]: { ...(prev.pricing_roles[userTypeId] || { max_qty_per_booking: '', max_qty_per_day: '' }), [field]: value },
      },
    }));
  };

  const updateDefaultUserTypeId = (userTypeId: string) => {
    setFormData((prev) => ({ ...prev, pricing_default_user_type_id: userTypeId }));
  };

  // SAM-194 step 1.5 — fire the booking-engine sync after a successful save. Best-effort: the
  // sync's own toast (positive or warning, see useSyncServiceToEngine) is the feedback; a failed
  // sync never blocks the save that already succeeded. No-op entirely when the flag is off.
  const triggerEngineSync = (savedServiceId: string | undefined | null) => {
    if (!isBookingEngineSyncEnabled() || !savedServiceId) return;
    syncServiceToEngine.mutate(savedServiceId);
  };

  const save = (onSuccess?: (createdId?: string) => void) => {
    const data = formToServiceData(formData, mode, mode === 'edit' ? service : null);
    setSeasonServerIssue(null);
    setBadgeServerIssue(null);
    setSaveError(null);
    // SAM-626 — the CRUD 400 carries `code` + `details.seasons`; keep it so the rows can show it.
    const onSeasonError = (error: unknown) => {
      setSeasonServerIssue(seasonSaveIssue(error));
      setBadgeServerIssue(badgeSaveIssue(error));
      if (!seasonSaveIssue(error) && !badgeSaveIssue(error)) setSaveError(getApiErrorMessage(error, 'The service could not be saved.'));
    };

    if (mode === 'create') {
      createService.mutate(data, {
        onSuccess: (response) => {
          // Defensive extraction — the CRUD lambda's create response envelope isn't a typed
          // contract this editor otherwise depends on, so a shape miss just skips the auto-sync
          // (the "Booking engine" status block's manual "Sync now" button still covers it once
          // the service is reopened in edit mode).
          const createdId = createdServiceId(response);
          triggerEngineSync(createdId);
          setInitialSnapshot(JSON.stringify(formData));
          onSuccess?.(createdId);
        },
        onError: onSeasonError,
      });
    } else if (service) {
      updateService.mutate({ id: service.id, data }, {
        onSuccess: () => {
          triggerEngineSync(service.id);
          setInitialSnapshot(JSON.stringify(formData));
          onSuccess?.(service.id);
        },
        onError: onSeasonError,
      });
    }
  };

  const remove = (onSuccess?: () => void) => {
    if (!service) return;
    deleteService.mutate(service.id, { onSuccess });
  };

  const isSaving = createService.isPending || updateService.isPending;
  const isDeleting = deleteService.isPending;

  // SAM-545 — a service with no booking options is implicitly free (unchanged
  // default behaviour); one with options is "paid" and each priced option
  // needs a non-negative price and a label a guest can read. Kept separate
  // from isFormValid's message so an invalid price can point at the exact
  // option instead of a generic "can't save".
  const isPaid = formData.booking_options.length > 0;
  const isUnitPriced = formData.price_unit === 'unit';
  // SAM-592 — event services price by size band (prices_by_event_size), so the flat per-ticket
  // price is not required for them. Driven by the pricing-model selector (price_unit), not the
  // subtype: an event subtype only DEFAULTS to it (applySubtypeDefaults).
  const isEventForm = formData.price_unit === 'event';
  const invalidPriceIndexes = new Set(
    formData.booking_options
      .map((opt, i) => (!isEventForm && opt.label_en.trim() && (opt.price.trim() === '' || Number.isNaN(Number(opt.price)) || Number(opt.price) < 0) ? i : -1))
      .filter((i) => i >= 0)
  );
  // SAM-589 — season dates/overlap/price validation (mirrors pricing.validate_seasons).
  const clientSeasonErrors = validateSeasonForms(formData.seasons);
  // SAM-626 — a rejected save (season_overlap / invalid_season) names the seasons by id; show it on
  // those rows too, in labels, until the seasons change. The client check normally catches it first.
  const seasonErrors = { ...clientSeasonErrors, bySeason: { ...clientSeasonErrors.bySeason } };
  if (seasonServerIssue) {
    const labelOf = (id: string) => {
      const row = formData.seasons.find((s) => s.id === id);
      return row?.label_en || row?.label_es || id;
    };
    const message = formatSeasonSaveIssue(seasonServerIssue, labelOf);
    seasonServerIssue.ids.forEach((id) => {
      const index = formData.seasons.findIndex((s) => s.id === id);
      if (index >= 0) seasonErrors.bySeason[index] = [...(seasonErrors.bySeason[index] || []), message];
    });
    seasonErrors.hasErrors = true;
  }
  // First season problem, worded for the save bar ("Season 2: Overlaps another season (High)").
  const firstSeasonIndex = Object.keys(seasonErrors.bySeason).map(Number).sort((a, b) => a - b)[0];
  const seasonBlockMessage = firstSeasonIndex === undefined
    ? null
    : `Season ${firstSeasonIndex + 1}: ${seasonErrors.bySeason[firstSeasonIndex][0]}`;
  // SAM-591 — in unit mode the per-option `price` is not used, so it cannot be invalid; the unit
  // price / included / max pax and each option's extra-guest price are validated instead.
  const unitIssues = isUnitPriced
    ? unitPricingIssues(formData, formData.resources, formData.booking_options)
    : [];
  if (isUnitPriced) invalidPriceIndexes.clear();
  const hasPricingErrors = invalidPriceIndexes.size > 0 || unitIssues.length > 0 || seasonErrors.hasErrors;

  // SAM-198 — per-day window validation: close after open for each extra
  // window, and windows of the same day must not overlap. Only enabled days
  // are checked, mirroring what's actually included in the save payload. A
  // day with no extras is left unvalidated by validateDayWindows itself —
  // that's what keeps an existing midnight-crossing day (open 18:00, close
  // 02:00, no extras) saveable, exactly as before this feature existed.
  const operatingHourErrors: Record<string, string[]> = {};
  Object.entries(formData.operating_hours).forEach(([day, config]) => {
    // SAM-656 — following the business: the own hours are hidden and only kept, never edited.
    if (!config.enabled || formData.hours_source === 'business') return;
    const windows: TimeWindow[] = [{ open: config.open, close: config.close }, ...config.extra_windows];
    const result = validateDayWindows(windows);
    if (!result.valid) operatingHourErrors[day] = result.errors;
  });
  const hasOperatingHourErrors = Object.keys(operatingHourErrors).length > 0;

  // SAM-466 — only checked while the field is visible (penalty fee on).
  const penaltyPercentIssue = formData.cancellation_penalty_fee
    ? penaltyPercentError(formData.cancellation_penalty_percent)
    : null;

  const isFormValid = formData.name_en.trim().length > 0
    && !penaltyPercentIssue
    && (mode === 'edit' || !!formData.business_id)
    && !hasPricingErrors
    && !hasOperatingHourErrors
    && !hasAddOnErrors;

  const isDirty = JSON.stringify(formData) !== initialSnapshot;

  return {
    formData,
    setFormData,
    updateField,
    updateSubtype,
    updateOperatingHour,
    addExtraWindow,
    removeExtraWindow,
    updateExtraWindow,
    addResource,
    removeResource,
    updateResource,
    addClosure,
    removeClosure,
    updateClosure,
    addTierGroup,
    removeTierGroup,
    updateTierGroup,
    toggleTierGroupOption,
    updatePriceUnit,
    unitCarryOver,
    addRateGroup,
    removeRateGroup,
    updateRateGroup,
    updateOptionGroupPrice,
    addBookingOption,
    removeBookingOption,
    updateBookingOption,
    addAddOn,
    removeAddOn,
    updateAddOn,
    reorderAddOn,
    updateAddOnRolePrice,
    addOnErrors,
    hasAddOnErrors,
    activeRoleIds,
    updateOptionRolePrice,
    addRoleColumn,
    removeRoleColumn,
    updateRoleCap,
    updateDefaultUserTypeId,
    addSeason,
    removeSeason,
    updateSeason,
    updateSeasonPrice,
    seasonErrors,
    badgeServerIssue,
    saveError,
    seasonBlockMessage,
    penaltyPercentIssue,
    updateSeasonEveryYear,
    save,
    remove,
    isSaving,
    isDeleting,
    isPaid,
    invalidPriceIndexes,
    unitIssues,
    isEventForm,
    updateOptionEventPrice,
    hasPricingErrors,
    operatingHourErrors,
    hasOperatingHourErrors,
    isFormValid,
    isDirty,
    showDeleteConfirm,
    setShowDeleteConfirm,
    expandedSections,
    toggleSection,
  };
}

export type ServiceEditorState = ReturnType<typeof useServiceEditor>;
